import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { bookingLink } from '../data/hotelData';
import logoImage from '../assets/miran-logo.png'; 

// --- Styled Components ---

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  padding: 0.5rem 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  transition: all 0.3s ease;
  
  background: ${props => (props.$scrolled || props.$isOpen) ? '#fff' : 'transparent'};
  box-shadow: ${props => (props.$scrolled && !props.$isOpen) ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'};
  color: ${props => (props.$scrolled || props.$isOpen) ? '#1a1a1a' : '#fff'};

  @media (max-width: 768px) {
    padding: 0.5rem 1.5rem;
    background: #fff; 
    color: #1a1a1a;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  z-index: 1002; 
`;

const LogoImg = styled.img`
  height: 60px;
  object-fit: contain;
  
  @media (max-width: 768px) {
    height: 50px;
  }
`;

const DesktopMenu = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none; 
  }
`;

// NEW: Styled Link with Active State Indicator
const NavLink = styled(Link)`
  color: ${props => props.$active ? '#d4af37' : 'inherit'}; /* Gold if active */
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s;
  position: relative;
  padding-bottom: 5px;

  /* Animated Underline */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.$active ? '100%' : '0'}; /* Full width if active */
    height: 2px;
    background: #d4af37;
    transition: width 0.3s ease;
  }

  &:hover { 
    color: #d4af37;
    &::after {
      width: 100%;
    }
  }
`;

const Hamburger = styled.div`
  display: none;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1002;
  color: #1a1a1a; 
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-100%)'};
  transition: transform 0.4s ease-in-out;
  z-index: 1001;
`;

const MobileLink = styled(Link)`
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  /* Active color logic for mobile */
  color: ${props => props.$active ? '#d4af37' : '#1a1a1a'};
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover { color: #d4af37; }
`;

// --- Component ---

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  // Helper to check active path
  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  return (
    <>
      <Nav $scrolled={scrolled || !isHome} $isOpen={isOpen}>
        <LogoContainer to="/">
          <LogoImg src={logoImage} alt="Miran Suites Logo" />
        </LogoContainer>

        {/* Desktop Menu */}
        <DesktopMenu>
          <NavLink to="/" $active={isActive('/')}>Home</NavLink>
          <NavLink to="/rooms" $active={isActive('/rooms')}>Rooms</NavLink>
          <NavLink to="/about" $active={isActive('/about')}>About</NavLink>
          <NavLink to="/contact" $active={isActive('/contact')}>Contact</NavLink>
          <Button 
            type="primary" 
            href={bookingLink} 
            target="_blank"
            style={{ 
              background: '#d4af37', 
              borderColor: '#d4af37',
              color: '#fff',
              borderRadius: '0'
            }}
          >
            BOOK NOW
          </Button>
        </DesktopMenu>

        {/* Hamburger Icon */}
        <Hamburger onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <CloseOutlined /> : <MenuOutlined />}
        </Hamburger>
      </Nav>

      {/* Mobile Full Screen Menu */}
      <MobileMenuOverlay $isOpen={isOpen}>
        <MobileLink to="/" $active={isActive('/')}>Home</MobileLink>
        <MobileLink to="/rooms" $active={isActive('/rooms')}>Rooms</MobileLink>
        <MobileLink to="/about" $active={isActive('/about')}>About</MobileLink>
        <MobileLink to="/contact" $active={isActive('/contact')}>Contact</MobileLink>
        <Button 
          type="primary" 
          size="large"
          href={bookingLink} 
          target="_blank"
          style={{ 
            marginTop: '1rem',
            background: '#d4af37', 
            borderColor: '#d4af37',
            minWidth: '200px'
          }}
        >
          BOOK NOW
        </Button>
      </MobileMenuOverlay>
    </>
  );
};

export default Navbar;